/**
 * Version-to-version diff of a firewall rule document (#1760).
 *
 * Rollback is "activate an older version", which is a one-click change to what
 * every live request sees. The operator therefore needs to read what actually
 * differs between the running version and the one they are about to point at —
 * and position is part of that, because evaluation is first match wins, so a
 * rule that only MOVED can change a verdict without a single character of its
 * own changing.
 *
 * Rules are matched by `id`, not by position, for the same reason: matching by
 * position would report a one-rule insertion at the top as "every rule changed"
 * and hide the one thing that happened.
 */

export type FirewallRuleDiffEntry =
  | { readonly kind: 'added'; readonly id: string; readonly toIndex: number }
  | { readonly kind: 'removed'; readonly id: string; readonly fromIndex: number }
  | {
    readonly kind: 'changed';
    readonly id: string;
    readonly fromIndex: number;
    readonly toIndex: number;
    /** Modelled rule fields whose canonical value differs. Never empty. */
    readonly fields: readonly string[];
  }
  | {
    readonly kind: 'moved';
    readonly id: string;
    readonly fromIndex: number;
    readonly toIndex: number;
  }
  | { readonly kind: 'unchanged'; readonly id: string; readonly index: number };

const COMPARED_FIELDS = ['description', 'action', 'deny', 'synthesize', 'when'] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Stable stringification, so key order in the stored JSON cannot masquerade as
 * a change. `undefined` and a missing key are the same absence here — that is
 * what the wire round trip does to them anyway.
 */
function canonical(value: unknown): string {
  if (value === undefined) return 'undefined';
  if (Array.isArray(value)) {
    return `[${value.map((item) => canonical(item)).join(',')}]`;
  }
  if (isRecord(value)) {
    const keys = Object.keys(value).sort();
    return `{${keys.map((key) => `${JSON.stringify(key)}:${canonical(value[key])}`).join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

interface IndexedRule {
  readonly index: number;
  readonly rule: Record<string, unknown>;
}

/** `null` when either side is not a readable rule array. */
function indexById(document: unknown): Map<string, IndexedRule> | null {
  if (!Array.isArray(document)) return null;
  const byId = new Map<string, IndexedRule>();
  for (const [index, rule] of document.entries()) {
    if (!isRecord(rule)) return null;
    const id = rule.id;
    // A document with an unusable or repeated id has no rule-to-rule
    // correspondence to compute, and a diff that silently paired the wrong
    // rules would be worse than no diff at all.
    if (typeof id !== 'string' || id.length === 0 || byId.has(id)) return null;
    byId.set(id, { index, rule });
  }
  return byId;
}

function changedFields(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
): readonly string[] {
  return COMPARED_FIELDS.filter((field) => canonical(before[field]) !== canonical(after[field]));
}

/**
 * Diff two rule documents, or `null` when either cannot be read as a rule array
 * with usable ids.
 *
 * Ordered by the AFTER document so the result reads as the chain the operator
 * is about to activate; removed rules are appended at the end, since they have
 * no position in it.
 */
export function diffFirewallRules(
  before: unknown,
  after: unknown,
): readonly FirewallRuleDiffEntry[] | null {
  const beforeById = indexById(before);
  const afterById = indexById(after);
  if (beforeById === null || afterById === null) return null;

  const entries: FirewallRuleDiffEntry[] = [];
  for (const [id, current] of afterById) {
    const previous = beforeById.get(id);
    if (previous === undefined) {
      entries.push({ kind: 'added', id, toIndex: current.index });
      continue;
    }
    const fields = changedFields(previous.rule, current.rule);
    if (fields.length > 0) {
      entries.push({
        kind: 'changed',
        id,
        fromIndex: previous.index,
        toIndex: current.index,
        fields,
      });
    } else if (previous.index !== current.index) {
      entries.push({ kind: 'moved', id, fromIndex: previous.index, toIndex: current.index });
    } else {
      entries.push({ kind: 'unchanged', id, index: current.index });
    }
  }
  for (const [id, previous] of beforeById) {
    if (!afterById.has(id)) {
      entries.push({ kind: 'removed', id, fromIndex: previous.index });
    }
  }
  return entries;
}

/** True when the diff contains nothing an operator needs to look at. */
export function firewallDiffIsEmpty(entries: readonly FirewallRuleDiffEntry[]): boolean {
  return entries.every((entry) => entry.kind === 'unchanged');
}
